import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, Lock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import temwiseLogo from "@/assets/temwise-logo.png";
import loginCityscape from "@/assets/login-cityscape.jpg";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    // Check if we have a valid session from the reset link
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        toast({
          title: "Error",
          description: "Invalid or expired reset link",
          variant: "destructive",
          duration: 3000,
        });
        navigate("/signin");
      }
    });
  }, [navigate, toast]);

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password.length < 6) {
      toast({
        title: "Error",
        description: "Password must be at least 6 characters",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: "Error",
        description: "Passwords do not match",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    setLoading(true);

    const { error } = await supabase.auth.updateUser({
      password: password,
    });

    if (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to reset password",
        variant: "destructive",
        duration: 3000,
      });
      setLoading(false);
    } else {
      toast({
        title: "Success",
        description: "Password updated successfully! Redirecting to sign in...",
        duration: 3000,
      });
      
      // Sign out and redirect to sign in
      await supabase.auth.signOut();
      setTimeout(() => {
        navigate("/signin");
      }, 1500);
    }
  };

  return (
    <div className="flex h-screen">
      {/* Left side - Reset form */}
      <div className="w-1/2 flex flex-col justify-center items-center px-6 py-6 bg-[#042cc4] text-white">
        <div className="w-full max-w-md space-y-4">
          <div className="flex justify-center">
            <img 
              src={temwiseLogo} 
              alt="Temwise Logo"
              className="h-20 w-auto"
            />
          </div>
          
          <div className="space-y-4">
            <h1 className="text-xl font-bold text-center">Reset Your Password</h1>
            <p className="text-center text-gray-400 text-xs">Enter your new password below</p>
            
            <form onSubmit={handlePasswordReset} className="space-y-3">
              <div>
                <label className="text-xs text-gray-400 mb-1 block">New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter new password (min 6 characters)"
                    className="pl-10 bg-gray-900 border-gray-700 text-white h-9"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-400 mb-1 block">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Confirm new password"
                    className="pl-10 bg-gray-900 border-gray-700 text-white h-9"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-white text-[#042cc4] hover:bg-gray-100 h-9"
                disabled={loading}
              >
                {loading ? "Resetting..." : "Reset Password"}
              </Button>
            </form>
          </div>
        </div>
      </div>
      
      {/* Right side - Graphic */}
      <div className="w-1/2 flex items-center justify-center p-0 bg-white overflow-hidden">
        <img 
          src={loginCityscape}
          alt="City skyline"
          className="w-full h-full object-cover"
        />
      </div>
    </div>
  );
};

export default ResetPassword;
